import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';

const BACKEND = 'https://alphabioai-backend.onrender.com';

const T = {
  bg:        '#0c0c0e',
  surface:   '#141416',
  surfaceHi: '#1c1c1f',
  border:    '#2e2e32',
  borderHi:  '#48484f',
  text:      '#f0f0f2',
  textSub:   '#a8a8b3',
  textMute:  '#6e6e7a',
  blue:      '#3b82f6',
  blueDim:   'rgba(59,130,246,0.10)',
  green:     '#22c55e',
  amber:     '#f59e0b',
  red:       '#ef4444',
  purple:    '#a78bfa',
};

const fmt$ = (n) =>
  n != null
    ? '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

const darkInput = {
  padding: '8px 12px',
  border: '1px solid #2e2e32',
  borderRadius: '6px',
  fontSize: '13px',
  color: '#f0f0f2',
  backgroundColor: '#0c0c0e',
  outline: 'none',
  height: '36px',
  boxSizing: 'border-box',
  display: 'block',
  transition: 'border-color 0.15s',
};

function DarkField({ label, children }) {
  return (
    <div>
      <div style={{
        fontSize: '11px', fontWeight: '700', color: T.textMute,
        marginBottom: '5px', textTransform: 'uppercase',
        letterSpacing: '0.8px', fontFamily: "'DM Mono', monospace",
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function AlertTypeBadge({ type }) {
  const styles = {
    above:    { color: T.green,  bg: 'rgba(34,197,94,0.10)',  label: '▲ ABOVE'    },
    below:    { color: T.red,    bg: 'rgba(239,68,68,0.10)',   label: '▼ BELOW'    },
    pct_move: { color: T.amber,  bg: 'rgba(245,158,11,0.10)', label: '% MOVE'     },
  };
  const s = styles[type] || styles.above;
  return (
    <span style={{
      fontSize: '11px', fontWeight: '700',
      color: s.color, backgroundColor: s.bg,
      padding: '3px 8px', borderRadius: '4px',
      fontFamily: "'DM Mono', monospace",
    }}>
      {s.label}
    </span>
  );
}

export default function PriceAlerts({ session, plan }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    ticker: '',
    alert_type: 'below',
    threshold: '',
  });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const isMobile = windowWidth < 768;
  const STARTER_LIMIT = 10;

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('price_alerts')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      if (!error) setAlerts(data || []);
    } catch (e) {
      console.error('Error fetching alerts:', e);
    }
    setLoading(false);
  }, [session]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleAdd = async () => {
    const { ticker, alert_type, threshold } = form;
    if (!ticker.trim()) { setAddError('Ticker is required.'); return; }
    if (!threshold || isNaN(parseFloat(threshold)) || parseFloat(threshold) <= 0) {
      setAddError('Enter a valid threshold (must be greater than 0).'); return;
    }
    if (plan === 'starter' && alerts.length >= STARTER_LIMIT) {
      setAddError(`Starter plan limit: ${STARTER_LIMIT} alerts. Upgrade to Pro for unlimited.`); return;
    }

    setAddLoading(true);
    setAddError('');

    // Try to get company name from backend
    const tickerUpper = ticker.toUpperCase().trim();
    let company_name = tickerUpper;
    try {
      const res = await fetch(`${BACKEND}/api/stock-price/${tickerUpper}`);
      const data = await res.json();
      if (!data.error && data.company_name) company_name = data.company_name;
    } catch {}

    try {
      const res = await fetch(`${BACKEND}/api/price-alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: session.user.id,
          email: session.user.email,
          ticker: tickerUpper,
          company_name,
          alert_type,
          threshold: parseFloat(threshold),
        }),
      });
      const data = await res.json();
      if (data.error) {
        setAddError(data.error);
      } else {
        setForm({ ticker: '', alert_type: 'below', threshold: '' });
        await fetchAlerts();
      }
    } catch (e) {
      setAddError('Failed to create alert. Please try again.');
    }
    setAddLoading(false);
  };

  const handleDelete = async (alertId) => {
    try {
      await fetch(`${BACKEND}/api/price-alerts/${alertId}?user_id=${session.user.id}`, {
        method: 'DELETE',
      });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (e) {
      console.error('Error deleting alert:', e);
    }
  };

  const getThresholdLabel = (alert) => {
    if (alert.alert_type === 'pct_move') {
      return `±${alert.threshold}% daily move`;
    }
    if (alert.alert_type === 'above') {
      return `Price above ${fmt$(alert.threshold)}`;
    }
    return `Price below ${fmt$(alert.threshold)}`;
  };

  const getAlertColor = (alert) => {
    if (alert.alert_type === 'above') return T.green;
    if (alert.alert_type === 'below') return T.red;
    return T.amber;
  };

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: '600', color: T.text, margin: '0 0 4px' }}>
          Price Alerts
        </h2>
        <p style={{ color: T.textSub, fontSize: '14px', margin: 0 }}>
          Get emailed when a biotech stock crosses your price threshold or moves significantly.
          {plan === 'starter' && (
            <span style={{ color: T.textMute }}> {alerts.length}/{STARTER_LIMIT} alerts used.</span>
          )}
        </p>
      </div>

      {/* Summary cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)',
        gap: '8px',
        marginBottom: '20px',
      }}>
        {[
          { label: 'Active Alerts',   value: alerts.length,                                              color: T.blue  },
          { label: 'Price Threshold', value: alerts.filter(a => a.alert_type !== 'pct_move').length,     color: T.amber },
          { label: '% Move Alerts',   value: alerts.filter(a => a.alert_type === 'pct_move').length,     color: T.purple },
        ].map((s, i) => (
          <div key={i} style={{
            backgroundColor: T.surface, border: `1px solid ${T.border}`,
            borderRadius: '8px', padding: '14px 16px',
          }}>
            <div style={{
              fontSize: '11px', color: T.textMute, fontWeight: '700',
              marginBottom: '6px', textTransform: 'uppercase',
              letterSpacing: '1px', fontFamily: "'DM Mono', monospace",
            }}>{s.label}</div>
            <div style={{
              fontSize: '18px', fontWeight: '700', color: s.color,
              fontFamily: "'DM Mono', monospace", letterSpacing: '-0.5px',
            }}>
              {loading ? <span style={{ color: T.textMute }}>—</span> : s.value}
            </div>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div style={{
        marginBottom: '20px', padding: '14px 18px',
        backgroundColor: T.surface, border: `1px solid ${T.border}`,
        borderRadius: '8px',
      }}>
        <div style={{ fontSize: '13px', color: T.textSub, lineHeight: '1.7' }}>
          <span style={{ color: T.blue, fontWeight: '700' }}>How it works:</span>{' '}
          Set a price threshold (e.g. "alert me when REPL drops below $8") or a % move alert (e.g. "alert me when any stock moves more than 10% in a day").
          You'll get an email the next morning when the threshold is crossed. Prices are checked daily using previous close data.
        </div>
      </div>

      {/* Add alert form */}
      <div style={{
        backgroundColor: T.surface, border: `1px solid ${T.border}`,
        borderRadius: '8px', padding: '16px', marginBottom: '20px',
      }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: T.text, margin: '0 0 12px' }}>
          Create Alert
        </h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>

          {/* Ticker */}
          <DarkField label="Ticker">
            <input
              value={form.ticker}
              onChange={e => setForm(f => ({ ...f, ticker: e.target.value.toUpperCase() }))}
              placeholder="MRNA"
              style={{ ...darkInput, width: isMobile ? '80px' : '100px' }}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
          </DarkField>

          {/* Alert type */}
          <DarkField label="Alert Type">
            <select
              value={form.alert_type}
              onChange={e => setForm(f => ({ ...f, alert_type: e.target.value }))}
              style={{
                ...darkInput,
                width: isMobile ? '130px' : '150px',
                cursor: 'pointer',
              }}
            >
              <option value="below">Price drops below</option>
              <option value="above">Price rises above</option>
              <option value="pct_move">% move in a day</option>
            </select>
          </DarkField>

          {/* Threshold */}
          <DarkField label={form.alert_type === 'pct_move' ? 'Move % (e.g. 10)' : 'Price ($)'}>
            <input
              type="number"
              min="0"
              step={form.alert_type === 'pct_move' ? '1' : '0.01'}
              value={form.threshold}
              onChange={e => setForm(f => ({ ...f, threshold: e.target.value }))}
              placeholder={form.alert_type === 'pct_move' ? '10' : '8.00'}
              style={{ ...darkInput, width: isMobile ? '90px' : '110px' }}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
          </DarkField>

          <button
            onClick={handleAdd}
            disabled={addLoading || (plan === 'starter' && alerts.length >= STARTER_LIMIT)}
            style={{
              backgroundColor:
                addLoading || (plan === 'starter' && alerts.length >= STARTER_LIMIT)
                  ? T.textMute : T.blue,
              color: 'white', border: 'none', borderRadius: '6px',
              padding: '0 16px', height: '36px', fontSize: '13px',
              fontWeight: '600',
              cursor: addLoading || (plan === 'starter' && alerts.length >= STARTER_LIMIT)
                ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {addLoading ? '...' : '+ Add Alert'}
          </button>
        </div>

        {addError && (
          <p style={{ color: T.red, fontSize: '13px', margin: '10px 0 0' }}>⚠ {addError}</p>
        )}

        {plan === 'starter' && alerts.length >= STARTER_LIMIT && (
          <p style={{ color: T.amber, fontSize: '13px', margin: '10px 0 0' }}>
            ★ Starter limit reached ({STARTER_LIMIT} alerts).{' '}
            <span style={{ color: T.purple, fontWeight: '600' }}>Upgrade to Pro for unlimited alerts.</span>
          </p>
        )}
      </div>

      {/* Alerts list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: T.textMute }}>
          <div style={{ fontSize: '14px', marginBottom: '12px' }}>Loading alerts...</div>
          <div style={{ width: '80px', height: '2px', backgroundColor: T.border, borderRadius: '1px', overflow: 'hidden', margin: '0 auto' }}>
            <div style={{ height: '100%', width: '50%', backgroundColor: T.blue, animation: 'loadBar 1.4s ease-in-out infinite' }} />
          </div>
        </div>
      ) : alerts.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '50px 24px',
          backgroundColor: T.surface, borderRadius: '8px',
          border: `1px dashed ${T.border}`,
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px', color: T.textMute }}>🔔</div>
          <div style={{ fontWeight: '600', fontSize: '15px', color: T.textSub, marginBottom: '6px' }}>No alerts yet</div>
          <div style={{ fontSize: '13px', color: T.textMute, maxWidth: '320px', margin: '0 auto', lineHeight: 1.6 }}>
            Create your first price alert above to get notified when a stock hits your target.
          </div>
        </div>
      ) : isMobile ? (
        /* ── MOBILE: Card layout ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {alerts.map(alert => {
            const alertColor = getAlertColor(alert);
            return (
              <div key={alert.id} style={{
                backgroundColor: T.surface,
                border: `1px solid ${T.border}`,
                borderLeft: `3px solid ${alertColor}`,
                borderRadius: '8px',
                padding: '14px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: '700', color: T.blue, fontSize: '16px', fontFamily: "'DM Mono', monospace" }}>
                      {alert.ticker}
                    </span>
                    <AlertTypeBadge type={alert.alert_type} />
                  </div>
                  <button
                    onClick={() => handleDelete(alert.id)}
                    style={{ background: 'none', border: 'none', color: T.textMute, cursor: 'pointer', fontSize: '14px', padding: '4px' }}
                    onMouseEnter={e => e.currentTarget.style.color = T.red}
                    onMouseLeave={e => e.currentTarget.style.color = T.textMute}
                  >✕</button>
                </div>
                <div style={{ fontSize: '13px', color: T.textSub, marginBottom: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {alert.company_name || alert.ticker}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: T.textMute, marginBottom: '2px', fontFamily: "'DM Mono', monospace" }}>CONDITION</div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: alertColor }}>{getThresholdLabel(alert)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: T.textMute, marginBottom: '2px', fontFamily: "'DM Mono', monospace" }}>CURRENT PRICE</div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: T.text, fontFamily: "'DM Mono', monospace" }}>
                      {alert.current_price ? fmt$(alert.current_price) : '—'}
                    </div>
                  </div>
                </div>
                {alert.triggered_at && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: T.amber }}>
                    ⚡ Last triggered: {new Date(alert.triggered_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ── DESKTOP: Table layout ── */
        <div style={{ backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '90px 1fr 120px 150px 120px 120px 40px',
            padding: '11px 16px',
            backgroundColor: T.surfaceHi,
            borderBottom: `1px solid ${T.border}`,
          }}>
            {['Ticker', 'Company', 'Type', 'Condition', 'Current Price', 'Last Triggered', ''].map(h => (
              <div key={h} style={{
                fontSize: '11px', fontWeight: '700', color: T.textSub,
                textTransform: 'uppercase', letterSpacing: '0.8px',
                fontFamily: "'DM Mono', monospace",
              }}>{h}</div>
            ))}
          </div>

          {alerts.map((alert, i) => {
            const alertColor = getAlertColor(alert);
            return (
              <div
                key={alert.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '90px 1fr 120px 150px 120px 120px 40px',
                  padding: '13px 16px',
                  borderBottom: i < alerts.length - 1 ? `1px solid ${T.border}` : 'none',
                  alignItems: 'center',
                  borderLeft: `3px solid ${alertColor}`,
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = T.surfaceHi}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{ fontWeight: '700', color: T.blue, fontSize: '14px', fontFamily: "'DM Mono', monospace" }}>
                  {alert.ticker}
                </div>
                <div style={{ fontSize: '13px', color: T.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px' }}>
                  {alert.company_name || alert.ticker}
                </div>
                <div>
                  <AlertTypeBadge type={alert.alert_type} />
                </div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: alertColor }}>
                  {getThresholdLabel(alert)}
                </div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: T.text, fontFamily: "'DM Mono', monospace" }}>
                  {alert.current_price ? fmt$(alert.current_price) : '—'}
                </div>
                <div style={{ fontSize: '12px', color: alert.triggered_at ? T.amber : T.textMute }}>
                  {alert.triggered_at
                    ? new Date(alert.triggered_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : 'Not yet'}
                </div>
                <button
                  onClick={() => handleDelete(alert.id)}
                  style={{ background: 'none', border: 'none', color: T.textMute, cursor: 'pointer', fontSize: '15px', padding: '4px', lineHeight: 1 }}
                  onMouseEnter={e => e.currentTarget.style.color = T.red}
                  onMouseLeave={e => e.currentTarget.style.color = T.textMute}
                >✕</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Disclaimer */}
      <div style={{ marginTop: '20px', padding: '12px 16px', backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: '8px' }}>
        <div style={{ fontSize: '13px', color: T.textSub, lineHeight: 1.6 }}>
          ⚠️ <strong style={{ color: T.text }}>Note:</strong> Price alerts use previous close data from Polygon.io, checked once daily. Alerts are not real-time. Not financial advice.
        </div>
      </div>

      <style>{`
        @keyframes loadBar { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
        input::placeholder { color: #6e6e7a; }
        select option { background: #141416; color: #f0f0f2; }
      `}</style>
    </div>
  );
}